import React, { useState } from 'react';
import type { Transaction } from '../../types/transaction';
import { useDeleteTransaction } from '../../hooks/useTransactions';
import { useToast } from '../../lib/toast';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Modal } from '../ui/Modal';
import { TransactionForm } from './TransactionForm';
import { formatCurrency } from '../../lib/currency';

interface TransactionRowProps {
    transaction: Transaction;
}

export function TransactionRow({ transaction }: TransactionRowProps) {
    const deleteTransaction = useDeleteTransaction();
    const toast = useToast();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleEditClick = () => {
        setShowEditModal(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteTransaction.mutateAsync(transaction.id);
            toast.success('Transaction deleted successfully');
        } catch (err) {
            toast.error('Failed to delete transaction');
        }
    };

    const handleEditSuccess = () => {
        setShowEditModal(false);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div className="flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-4 flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    transaction.type === 'income'
                        ? 'bg-green-100 text-green-600'
                        : 'bg-red-100 text-red-600'
                }`}>
                    {transaction.type === 'income' ? '↑' : '↓'}
                </div>

                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                            {transaction.category || 'Uncategorized'}
                        </span>
                        {transaction.category && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                transaction.type === 'income'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                            }`}>
                                {transaction.type}
                            </span>
                        )}
                    </div>
                    {transaction.description && (
                        <p className="text-sm text-gray-500 mt-0.5">{transaction.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{formatDate(transaction.transaction_date)}</p>
                </div>
            </div>

            <div className="flex items-center gap-3">
                <div className={`text-right font-semibold ${
                    transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                }`}>
                    {transaction.type === 'income' ? '+' : '-'} {formatCurrency(transaction.amount)}
                </div>

                <Button
                    variant="secondary"
                    onClick={handleEditClick}
                    className="text-sm px-3 py-1"
                >
                    Edit
                </Button>

                <Button
                    variant="danger"
                    onClick={handleDeleteClick}
                    disabled={deleteTransaction.isPending}
                    className="text-sm px-3 py-1"
                >
                    Delete
                </Button>
            </div>

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Transaction"
                message={
                    <>
                        Are you sure you want to delete this transaction?
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                            <p className="font-medium text-gray-900">{transaction.category || 'Uncategorized'}</p>
                            <p className="text-sm text-gray-600 mt-1">
                                {transaction.type === 'income' ? '+' : '-'} {formatCurrency(transaction.amount)}
                            </p>
                        </div>
                    </>
                }
                confirmText="Delete"
                variant="danger"
                isLoading={deleteTransaction.isPending}
            />

            <Modal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                title="Edit Transaction"
            >
                <TransactionForm
                    onSuccess={handleEditSuccess}
                    initialData={transaction}
                    mode="edit"
                />
            </Modal>
        </div>
    );
}
